import { useCallback } from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import {
  DealLaScAdvisorShellKeys,
  DealLaScDealTermsPartnerAxisTableShellKeys,
  DealLaScMainFinancialTermsTableShellKeys,
  DealLaScMilestonePaymentShellKeys,
  DealLaScOtherPaymentShellKeys,
  DealLaScPartnerAShellKeys,
  DealLaScPartnerBShellKeys,
  DealLaScPartnerCShellKeys,
  DealLaScRoyaltyShellKeys,
  DealLaScSection,
} from "@/types/dealLaSc.types";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorShellReturn {
  handleClickToClearLicenseeTableShell: () => void;
  handleKeyDownToClearLicenseeTableShell: (event: KeyboardEvent) => void;
}

interface PositionInfoProps {
  groupTableKey: DealLaScSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;

  subGroupShellIndex: number | null;
  subGroupShellKey: string | null;
  tableKey: string | null;
  tableIndex: number;
  shellKey: string;
  shellIndex: number;
}

export const useHandleDealsLicenseeTableShell = (
  positionPropsInfo: PositionInfoProps,
): UseHandleTableCreatorShellReturn => {
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());

  const handleClearShell = useCallback(() => {
    const financialTermsSingleShellKeys = [
      "Paying Partner Axis(LA&SC)",
      "Receiving Partner Axis(LA&SC)",
      "Upfront Payment(LA&SC)",
      "Total Payment(LA&SC)",
      "Disclosed Payment Sum(LA&SC)",
    ];
    const milestonePaymentShellKeys = [
      "Milestone Payment(LA&SC)",
      "Milestone Payment Detail(LA&SC)",
    ];
    const otherPaymentShellKeys = [
      "Other Payment(LA&SC)",
      "Other Payment Detail(LA&SC)",
    ];
    const royaltyShellKeys = ["Royalty Rate", "Royalty Detail"];
    const advisorShellKeys = ["Advisor Name", "Advisor Role", "Party Axis"];
    const partnerAShellKeys = [
      "Partner A Name",
      "Partner A Relationship Context",
    ];
    const partnerBShellKeys = [
      "Partner B Name",
      "Partner B Relationship Context",
    ];
    const partnerCShellKeys = [
      "Partner C Name",
      "Partner C Relationship Context",
    ];
    const partnerAxisSingleShellKeys = [
      "Partner A Responsibility & Rights",
      "Partner B Responsibility & Rights",
      "Partner C Responsibility & Rights",
    ];

    const otherFinancialTernsShellKeys = ["Other Financial Terms"];
    const clearFinancialTermsShell = (): ArticleEntryData => {
      if (
        !info.shellKey ||
        !financialTermsSingleShellKeys.includes(info.shellKey)
      ) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData?.[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (groupShellInfo, groupShellInfoIndex) => {
              if (groupShellInfoIndex === info.groupShellIndex) {
                return {
                  ...groupShellInfo,
                  [info.shellKey as DealLaScMainFinancialTermsTableShellKeys]: {
                    ...groupShellInfo?.[
                      info.shellKey as DealLaScMainFinancialTermsTableShellKeys
                    ],
                    value: [],
                  },
                };
              }

              return groupShellInfo;
            },
          ),
        },
      } as ArticleEntryData;
    };

    const clearMilestonePaymentShell = (): ArticleEntryData => {
      if (
        !info.shellKey ||
        !milestonePaymentShellKeys.includes(info.shellKey)
      ) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData?.[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (groupShellInfo, groupShellInfoIndex) => {
              if (groupShellInfoIndex === info.groupShellIndex) {
                return {
                  ...groupShellInfo,
                  "Milestone Payment And Detail": groupShellInfo?.[
                    "Milestone Payment And Detail"
                  ]?.map((subGroupShellInfo, subGroupShellInfoIndex) => {
                    if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                      return {
                        ...subGroupShellInfo,
                        [info.shellKey as DealLaScMilestonePaymentShellKeys]: {
                          ...subGroupShellInfo?.[
                            info.shellKey as DealLaScMilestonePaymentShellKeys
                          ],
                          value: [],
                        },
                      };
                    }

                    return subGroupShellInfo;
                  }),
                };
              }

              return groupShellInfo;
            },
          ),
        },
      } as ArticleEntryData;
    };

    const clearOtherPaymentShell = (): ArticleEntryData => {
      if (!info.shellKey || !otherPaymentShellKeys.includes(info.shellKey)) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData?.[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (groupShellInfo, groupShellInfoIndex) => {
              if (groupShellInfoIndex === info.groupShellIndex) {
                return {
                  ...groupShellInfo,
                  "Other Payment And Detail": groupShellInfo?.[
                    "Other Payment And Detail"
                  ]?.map((subGroupShellInfo, subGroupShellInfoIndex) => {
                    if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                      return {
                        ...subGroupShellInfo,
                        [info.shellKey as DealLaScOtherPaymentShellKeys]: {
                          ...subGroupShellInfo?.[
                            info.shellKey as DealLaScOtherPaymentShellKeys
                          ],
                          value: [],
                        },
                      };
                    }

                    return subGroupShellInfo;
                  }),
                };
              }

              return groupShellInfo;
            },
          ),
        },
      } as ArticleEntryData;
    };

    const clearRoyaltyShell = (): ArticleEntryData => {
      if (!info.shellKey || !royaltyShellKeys.includes(info.shellKey)) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Main Financial Terms Table": entryData?.[
            ENTRY_DATA_SECTION_KEYS.dealsLaSc
          ]?.["Main Financial Terms Table"]?.map(
            (groupShellInfo, groupShellInfoIndex) => {
              if (groupShellInfoIndex === info.groupShellIndex) {
                return {
                  ...groupShellInfo,
                  "Royalty(LA&SC)": groupShellInfo?.["Royalty(LA&SC)"]?.map(
                    (subGroupShellInfo, subGroupShellInfoIndex) => {
                      if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                        return {
                          ...subGroupShellInfo,
                          [info.shellKey as DealLaScRoyaltyShellKeys]: {
                            ...subGroupShellInfo?.[
                              info.shellKey as DealLaScRoyaltyShellKeys
                            ],
                            value: [],
                          },
                        };
                      }

                      return subGroupShellInfo;
                    },
                  ),
                };
              }

              return groupShellInfo;
            },
          ),
        },
      } as ArticleEntryData;
    };

    const clearPartnerAShell = (): ArticleEntryData => {
      if (!info.shellKey || !partnerAShellKeys.includes(info.shellKey)) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            "Partner A": entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Partner Axis"
            ]?.["Partner A"]?.map(
              (subGroupShellInfo, subGroupShellInfoIndex) => {
                if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                  return {
                    ...subGroupShellInfo,
                    [info.shellKey as DealLaScPartnerAShellKeys]: {
                      ...subGroupShellInfo?.[
                        info.shellKey as DealLaScPartnerAShellKeys
                      ],
                      value: [],
                    },
                  };
                }
                return subGroupShellInfo;
              },
            ),
          },
        },
      } as ArticleEntryData;
    };

    const clearPartnerBShell = (): ArticleEntryData => {
      if (!info.shellKey || !partnerBShellKeys.includes(info.shellKey)) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            "Partner B": entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Partner Axis"
            ]?.["Partner B"]?.map(
              (subGroupShellInfo, subGroupShellInfoIndex) => {
                if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                  return {
                    ...subGroupShellInfo,
                    [info.shellKey as DealLaScPartnerBShellKeys]: {
                      ...subGroupShellInfo?.[
                        info.shellKey as DealLaScPartnerBShellKeys
                      ],
                      value: [],
                    },
                  };
                }
                return subGroupShellInfo;
              },
            ),
          },
        },
      } as ArticleEntryData;
    };

    const clearPartnerCShell = (): ArticleEntryData => {
      if (!info.shellKey || !partnerCShellKeys.includes(info.shellKey)) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            "Partner C": entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Partner Axis"
            ]?.["Partner C"]?.map(
              (subGroupShellInfo, subGroupShellInfoIndex) => {
                if (subGroupShellInfoIndex === info.subGroupShellIndex) {
                  return {
                    ...subGroupShellInfo,
                    [info.shellKey as DealLaScPartnerCShellKeys]: {
                      ...subGroupShellInfo?.[
                        info.shellKey as DealLaScPartnerCShellKeys
                      ],
                      value: [],
                    },
                  };
                }
                return subGroupShellInfo;
              },
            ),
          },
        },
      } as ArticleEntryData;
    };

    const clearPartnerAxisSingleShell = (): ArticleEntryData => {
      if (
        !info.shellKey ||
        !partnerAxisSingleShellKeys.includes(info.shellKey)
      ) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"],
            [info.shellKey as DealLaScDealTermsPartnerAxisTableShellKeys]: {
              ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
                "Partner Axis"
              ]?.[info.shellKey as DealLaScDealTermsPartnerAxisTableShellKeys],
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };

    const clearAdvisorShell = (): ArticleEntryData => {
      if (!info.shellKey || !advisorShellKeys.includes(info.shellKey)) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Advisor Table": entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
            "Advisor Table"
          ]?.map((subGroupShellInfo, subGroupShellInfoIndex) => {
            if (subGroupShellInfoIndex === info.subGroupShellIndex) {
              return {
                ...subGroupShellInfo,
                [info.shellKey as DealLaScAdvisorShellKeys]: {
                  ...subGroupShellInfo?.[
                    info.shellKey as DealLaScAdvisorShellKeys
                  ],
                  value: [],
                },
              };
            }

            return subGroupShellInfo;
          }),
        },
      } as ArticleEntryData;
    };

    const clearOtherFinancialTermsShell = (): ArticleEntryData => {
      if (
        !info.shellKey ||
        !otherFinancialTernsShellKeys.includes(info.shellKey)
      ) {
        return entryData;
      }
      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Other Financial Terms": {
            ...entryData?.[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.[
              "Other Financial Terms"
            ],
            value: [],
          },
        },
      } as ArticleEntryData;
    };

    if (
      info.shellKey &&
      financialTermsSingleShellKeys.includes(info.shellKey)
    ) {
      setEntryData(clearFinancialTermsShell());
      changeCurrentHistory({
        entryData: clearFinancialTermsShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    if (info.shellKey && milestonePaymentShellKeys.includes(info.shellKey)) {
      setEntryData(clearMilestonePaymentShell());
      changeCurrentHistory({
        entryData: clearMilestonePaymentShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    if (info.shellKey && otherPaymentShellKeys.includes(info.shellKey)) {
      setEntryData(clearOtherPaymentShell());
      changeCurrentHistory({
        entryData: clearOtherPaymentShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    if (info.shellKey && royaltyShellKeys.includes(info.shellKey)) {
      setEntryData(clearRoyaltyShell());
      changeCurrentHistory({
        entryData: clearRoyaltyShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    if (info.shellKey && advisorShellKeys.includes(info.shellKey)) {
      setEntryData(clearAdvisorShell());
      changeCurrentHistory({
        entryData: clearAdvisorShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    if (info.shellKey && otherFinancialTernsShellKeys.includes(info.shellKey)) {
      setEntryData(clearOtherFinancialTermsShell());
      changeCurrentHistory({
        entryData: clearOtherFinancialTermsShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    if (info.shellKey && partnerAShellKeys.includes(info.shellKey)) {
      setEntryData(clearPartnerAShell());
      changeCurrentHistory({
        entryData: clearPartnerAShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    if (info.shellKey && partnerBShellKeys.includes(info.shellKey)) {
      setEntryData(clearPartnerBShell());
      changeCurrentHistory({
        entryData: clearPartnerBShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    if (info.shellKey && partnerCShellKeys.includes(info.shellKey)) {
      setEntryData(clearPartnerCShell());
      changeCurrentHistory({
        entryData: clearPartnerCShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    if (info.shellKey && partnerAxisSingleShellKeys.includes(info.shellKey)) {
      setEntryData(clearPartnerAxisSingleShell());
      changeCurrentHistory({
        entryData: clearPartnerAxisSingleShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  }, [
    changeCurrentHistory,
    changeFocusStatus,
    changeInfo,
    entryData,
    info,
    positionPropsInfo,
    setEntryData,
  ]);

  const handleClickToClearLicenseeTableShell = () => {
    if (focusStatus.type === "clear") {
      handleClearShell();
    }
  };

  const handleKeyDownToClearLicenseeTableShell = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearShell();
      }
    },
    [focusStatus.type, handleClearShell],
  );

  return {
    handleClickToClearLicenseeTableShell,
    handleKeyDownToClearLicenseeTableShell,
  };
};
